import React, { useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import { SubmitButton } from '@/components/ui/Buttons';
import Tooltip from '@/components/ui/Tooltip/TCITooltip';
import I18n from '@/modules/i18n';
import Axios from 'axios';
import * as Routes from 'modules/routes';
import PasswordPolicyCheckboxes, { PASSWORD_POLICY_USER_TYPES } from './PasswordPolicyCheckboxes';

interface Subscriber {
  id: number;
  strict_password_policies: string[];
}

interface StrictPasswordPolicySettingProps {
  disabled: boolean;
  subscriber: Subscriber;
}

const StrictPasswordPolicySetting = ({
  disabled,
  subscriber,
}: StrictPasswordPolicySettingProps) => {
  const [isEnabled, setIsEnabled] = useState(subscriber.strict_password_policies.length > 0);
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>(subscriber.strict_password_policies);
  const [lastSavedUserTypes, setLastSavedUserTypes] = useState<string[]>(subscriber.strict_password_policies);

  const isPristine = useMemo(() => {
    const current = [...selectedUserTypes].sort();
    const saved = [...lastSavedUserTypes].sort();

    if (current.length !== saved.length) return false;

    return current.every((type, idx) => type === saved[idx]);
  }, [selectedUserTypes, lastSavedUserTypes]);

  const { isError, isLoading, mutate: updateSubscriber } = useMutation({
    mutationFn: (userTypes: string[]) => {
      const data = {
        subscriber: {
          strict_password_policies: userTypes,
        },
      };
      return Axios.patch(Routes.plato_api_subscriber_path(subscriber.id), data);
    },
    onError: () => {
      setSelectedUserTypes(lastSavedUserTypes);
    },
    onSuccess: (_data, variables) => {
      setLastSavedUserTypes(variables);
    },
  });

  const handleSwitchChange = (checked: boolean) => {
    setIsEnabled(checked);

    const newTypes = checked ? PASSWORD_POLICY_USER_TYPES.map(type => type.value) : [];
    setSelectedUserTypes(newTypes);
  };

  const handleUserTypeChange = (userType: string, checked: boolean) => {
    if (!isEnabled) setIsEnabled(true);

    const updatedTypes = checked
      ? [...selectedUserTypes, userType]
      : selectedUserTypes.filter(type => type !== userType);

    setSelectedUserTypes(updatedTypes);
  };

  const switchId = 'strict-password-policy-switch';

  const renderSwitch = () => {
    const switchComponent = (
      <ReactSwitch
        aria-labelledby={switchId}
        checked={isEnabled}
        checkedIcon={false}
        disabled={disabled}
        handleDiameter={20}
        height={24}
        id={switchId}
        offColor="#cccccc"
        uncheckedIcon={false}
        width={48}
        onChange={handleSwitchChange}
        onColor="#522e91"
      />
    );

    if (disabled) {
      return (
        <Tooltip>
          <Tooltip.Trigger asChild>
            <div className="tw-inline">{switchComponent}</div>
          </Tooltip.Trigger>
          <Tooltip.Content>
            {I18n.t('only_district_admins_can_modify')}
          </Tooltip.Content>
        </Tooltip>
      );
    }

    return <div className="tw-inline">{switchComponent}</div>;
  };

  const renderLabelTooltip = () => {
    const tooltipContent = `This setting requires applicable users to
      create a password to match stricter requirements at their next sign-in.`;

    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <i
            aria-hidden="true"
            className="fa fa-question-circle slide-digital-only-tooltip tw-cursor-pointer"
            onClick={e => e.preventDefault()}
          />
        </Tooltip.Trigger>
        <Tooltip.Content>{tooltipContent}</Tooltip.Content>
      </Tooltip>
    );
  };

  const renderCheckboxesAndApply = () => (
    <div className="tw-ml-12 tw-mt-2">
      <PasswordPolicyCheckboxes
        disabled={disabled}
        idPrefix="strict-password-policy-user"
        selectedUserTypes={selectedUserTypes}
        onUserTypeChange={handleUserTypeChange}
      />
      <div className="tw-mt-2 tw-flex tw-items-center">
        <SubmitButton
          small
          disabled={isPristine}
          submitting={isLoading}
          onClick={() => updateSubscriber(selectedUserTypes)}
        >
          Apply
        </SubmitButton>
        {isError && (
          <span className="tw-text-red tw-ml-2">
            An error occurred while updating the subscriber.
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="tw-flex tw-items-center">
        {renderSwitch()}
        <label
          className="tw-ml-[5px] tw-cursor-pointer tw-font-normal"
          htmlFor={switchId}
        >
          Enable strict password policy {renderLabelTooltip()}
        </label>
      </div>
      {renderCheckboxesAndApply()}
    </div>
  );
};

const StrictPasswordPolicySettingWrapper = (props: StrictPasswordPolicySettingProps) => (
  <ReactQueryProvider>
    <StrictPasswordPolicySetting {...props} />
  </ReactQueryProvider>
);

export default StrictPasswordPolicySettingWrapper;
